import { useMemo } from 'react';

import CropSquareOutlined from '@mui/icons-material/CropSquareOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import { Box, Typography } from '@/shared/ui';

import { spacing } from '@/config/ui';
import { Permission } from '@/entities/permissions';
import ShareButton from '@/features/_shared/ShareButton';
import UpgradeToPremiumButton from '@/features/_shared/UpgradeToPremium/UpgradeToPremiumButton';
import { usePermission, useUserDateFormat } from '@/features/_shared/user';
import { FACTSHEETS_ENDPOINT } from '@/features/factsheet/constants';
import {
  formatFactsheetDate,
  getVersionHistoryField,
  getFactsheetLink
} from '@/features/factsheet/lib';
import type { FactsheetDetails } from '@/features/factsheet/types';
import { StorageKeys } from '@/services/storage';
import { useStorageValue } from '@/services/storage/react';
import downloadPDF from '@/shared/lib/download/downloadPDF';
import Button from '@/shared/ui/primitives/Button';

import useItemsTab from '../FactsheetBody/Tabs/ItemsTab/useItemsTab';

const btnRowSx = { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 };
const rootSx = {
  display: 'flex',
  alignItems: 'flex-start',
  flexDirection: 'column',
  px: 3,
  width: '100%'
};
const topRowSx = { display: 'flex', alignItems: 'center', width: '100%', flexWrap: 'wrap' };
const titleBoxSx = { flexGrow: 1, mr: spacing['6'], maxWidth: 740, minWidth: 0 };
const btnWrapSx = { display: 'flex', py: spacing['2'], flexShrink: 0 };
const versionSx = { pt: 1 };
const shareBtnSx = { width: '120px' };

interface ButtonSectionProps {
  factsheet: FactsheetDetails;
}

const ButtonSection = ({ factsheet }: ButtonSectionProps) => {
  const { handleAllItemsSelection, allItemsSelected } = useItemsTab(factsheet);

  const handleDownload = (): void => {
    downloadPDF(`${FACTSHEETS_ENDPOINT}/${factsheet.code}.pdf`, `${factsheet.code}.pdf`);
  };

  const { hasPermission } = usePermission();
  const isPermissionGranted = hasPermission(Permission.ViewIndexInformationChart);

  return (
    <Box sx={btnRowSx}>
      {!isPermissionGranted && <UpgradeToPremiumButton type='upgrade' />}
      <ShareButton
        iconSize={12}
        shareUrl={getFactsheetLink(factsheet.code)}
        sx={shareBtnSx}
        title={factsheet.title}
      />
      <Button
        color='secondary'
        disabled={allItemsSelected}
        size='medium'
        startIcon={<CropSquareOutlined />}
        variant='contained'
        onClick={handleAllItemsSelection}
      >
        Add item(s) to selector
      </Button>
      <Button
        color='secondary'
        size='medium'
        startIcon={<FileDownloadOutlined />}
        variant='contained'
        onClick={handleDownload}
      >
        Download factsheet
      </Button>
    </Box>
  );
};

interface DescriptionProps {
  description: string | undefined;
}

const Description = ({ description }: DescriptionProps) => {
  if (!description) return null;

  const lineCounts = new Map<string, number>();
  const lines = description.split('\n').map(line => {
    const occurrence = lineCounts.get(line) ?? 0;
    lineCounts.set(line, occurrence + 1);
    return {
      key: `${line || 'empty-line'}-${occurrence}`,
      text: line
    };
  });

  return (
    <Box>
      {lines.map(line => (
        <Typography
          key={line.key}
          sx={theme => ({
            mb: 0.5,
            fontSize: theme.typography.pxToRem(20)
          })}
        >
          {line.text}
        </Typography>
      ))}
    </Box>
  );
};

interface VersionInfoProps {
  version: string;
  validFrom: string;
  validTo: string;
}

const VersionInfo = ({ version, validFrom, validTo }: VersionInfoProps) => (
  <Typography
    sx={versionSx}
    variant='body1'
  >
    Factsheet version {version}
    {validFrom && `, valid from ${validFrom}`}
    {validTo && ` to ${validTo}`}
  </Typography>
);

interface FactsheetHeaderProps {
  factsheet: FactsheetDetails;
}

const FactsheetHeader = ({ factsheet }: FactsheetHeaderProps) => {
  const gxToken = useStorageValue(StorageKeys.GxToken);
  const dateFormat = useUserDateFormat();

  const validFrom = useMemo(
    () =>
      formatFactsheetDate(
        getVersionHistoryField(factsheet, 'factsheetValidFrom'),
        dateFormat.value
      ),
    [dateFormat.value, factsheet]
  );

  const validTo = useMemo(
    () =>
      formatFactsheetDate(getVersionHistoryField(factsheet, 'factsheetValidTo'), dateFormat.value),
    [dateFormat.value, factsheet]
  );

  return (
    <Box sx={rootSx}>
      <Box sx={topRowSx}>
        <Box sx={titleBoxSx}>
          <Typography variant='h3'>{factsheet.title}</Typography>
        </Box>
        <Box sx={btnWrapSx}>{gxToken && <ButtonSection factsheet={factsheet} />}</Box>
      </Box>
      <Description description={factsheet.description} />
      <VersionInfo
        validFrom={validFrom}
        validTo={validTo}
        version={factsheet.latestFactsheetVersion}
      />
    </Box>
  );
};

export default FactsheetHeader;
